import React from 'react';
import { 
  Box, 
  Flex, 
  Button, 
  IconButton,
  Image,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
  Text,
  Divider
} from '@chakra-ui/react';
import { FiMenu, FiChevronDown, FiUserPlus, FiMessageSquare, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Link } from 'react-router-dom';
import logoSrc from '../assets/logo.svg';
import './Header.css';

const Header = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Mock data for token prices - replace with real data later
  const tokenPrices = {
    desol: {
      price: 0.0045,
      change: 8.5
    },
    sol: {
      price: 150.78,
      change: 2.3
    }
  };

  return (
    <Box className="header" height={{ base: "58px", md: "65px" }}>
      <Flex
        align="center"
        justify="space-between"
        px={{ base: 2, md: 6 }}
        height="100%"
        maxW="1800px"
        mx="auto"
      >
        {/* Left Section: Mobile Menu Button and Logo */}
        <Flex align="center" gap={{ base: 2, md: 0 }}>
          {/* Mobile Menu Button */}
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            aria-label="Open menu"
            icon={<FiMenu size={20} />}
            onClick={onOpen}
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.200' }}
            size="sm"
            ml={{ base: "-2", md: 0 }}
          />
          
          <Link to="/" className="header-logo">
            <Image 
              src={logoSrc} 
              alt="Desol AI"
            />
          </Link>
        </Flex>

        {/* Center Section: Token Prices - Hidden on Mobile */}
        <Flex className="token-prices" display={{ base: 'none', md: 'flex' }}>
          <Box className="token-price">
            <Box className="token-price-content">
              <Text className="token-label">DESOL</Text>
              <Text className="token-value">
                ${tokenPrices.desol.price.toFixed(4)}
                <Text as="span" className="token-rate">
                  <Box as={FiArrowUp} />
                  {tokenPrices.desol.change}%
                </Text>
              </Text>
            </Box>
          </Box>
          <Box className="token-price">
            <Box className="token-price-content">
              <Text className="token-label">SOL</Text>
              <Text className="token-value">
                ${tokenPrices.sol.price.toFixed(2)}
                <Text as="span" className="token-rate">
                  <Box as={FiArrowUp} />
                  {tokenPrices.sol.change}%
                </Text>
              </Text>
            </Box>
          </Box>
        </Flex>

        {/* Right Section: Controls */}
        <HStack spacing={{ base: 1, md: 3 }}>

          {/* Actions Menu */}
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<FiChevronDown />}
              display={{ base: 'none', md: 'flex' }}
              bg="rgba(118, 120, 237, 0.1)"
              color="white"
              _hover={{ bg: 'rgba(118, 120, 237, 0.2)' }}
              _active={{ bg: 'rgba(118, 120, 237, 0.3)' }}
            >
              Actions
            </MenuButton>
            <MenuList bg="#24243e" borderColor="#30304e">
              <MenuItem 
                icon={<FiUserPlus />}
                _hover={{ bg: 'rgba(118, 120, 237, 0.1)' }}
                color="white"
              >
                Invite Users
              </MenuItem>
              <MenuItem 
                icon={<FiMessageSquare />}
                _hover={{ bg: 'rgba(118, 120, 237, 0.1)' }}
                color="white"
              >
                Request Features
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Wallet Button */}
          <Box className="wallet-button-container">
            <WalletMultiButton className="wallet-adapter-button" />
          </Box>
        </HStack>
      </Flex>

      {/* Mobile Menu Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="#24243e">
          <DrawerCloseButton color="white" />
          <DrawerHeader borderBottomWidth="1px" borderColor="#30304e">
            <Flex align="center">
              <Image src={logoSrc} alt="Desol AI" height="30px" />
            </Flex>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch" mt={4}>
              {/* Mobile Navigation Links */}
              <Link to="/" onClick={onClose} className="mobile-nav-link">Dashboard</Link>
              <Link to="/explorer" onClick={onClose} className="mobile-nav-link">Explorer</Link>
              <Link to="/ai" onClick={onClose} className="mobile-nav-link">Desol AI</Link>
              <Link to="/governance" onClick={onClose} className="mobile-nav-link">Governance</Link>
              <Link to="/wallet" onClick={onClose} className="mobile-nav-link">Wallet & Rewards</Link>
              <Link to="/token" onClick={onClose} className="mobile-nav-link">DESOL Token</Link>

              <Divider borderColor="#30304e" />

              {/* Token Prices in Mobile Menu */}
              <Box>
                <Text color="#a9a9c8" mb={2}>Token Prices</Text>
                <Flex justify="space-between" mb={2}>
                  <Text color="#a9a9c8">DESOL:</Text>
                  <Flex align="center" gap={2}>
                    <Text color="#00FF9D" fontWeight="bold">${tokenPrices.desol.price.toFixed(4)}</Text>
                    <Text color="#00FF9D" fontSize="sm">
                      <Box as={FiArrowUp} display="inline-block" mr={1} />
                      {tokenPrices.desol.change}%
                    </Text>
                  </Flex>
                </Flex>
                <Flex justify="space-between">
                  <Text color="#a9a9c8">SOL:</Text>
                  <Flex align="center" gap={2}>
                    <Text color="#00FF9D" fontWeight="bold">${tokenPrices.sol.price.toFixed(2)}</Text>
                    <Text color="#00FF9D" fontSize="sm">
                      <Box as={FiArrowUp} display="inline-block" mr={1} />
                      {tokenPrices.sol.change}%
                    </Text>
                  </Flex>
                </Flex>
              </Box>

              <Divider borderColor="#30304e" />

              {/* Mobile Actions */}
              <Text color="#a9a9c8">Actions</Text>
              <Link to="#" className="mobile-action" onClick={onClose}>
                <FiUserPlus style={{ marginRight: '8px' }} />
                Invite Users
              </Link>
              <Link to="#" className="mobile-action" onClick={onClose}>
                <FiMessageSquare style={{ marginRight: '8px' }} />
                Request Features
              </Link>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;